import { useCallback, useEffect, useState } from 'react'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import type { ContentViewerSession } from '@/content-viewer/store/schemas'
import { selectActiveContentViewerSession } from '@/content-viewer/store/selectors'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { TableReferenceTarget } from '../reference'
import { buildTableReferenceSession } from '../utils/build-table-reference-session'

/** Shared source selection and drawer lifecycle for standalone and chat tables. */
export function useTableSidebar(enabled: boolean, initiallyOpen: boolean, conversationId?: string) {
  const dispatch = useAppDispatch()
  const source = useAppSelector(selectActiveContentViewerSession)
  const [open, setOpen] = useState(initiallyOpen)
  const [width, setWidth] = useState(60)
  const [hiddenSource, setHiddenSource] = useState<ContentViewerSession | null>(null)
  const [selectedReference, setSelectedReference] = useState<{ id: string | null; session: ContentViewerSession } | null>(null)
  useEffect(() => {
    if (!enabled) return
    return () => {
      dispatch(ContentViewerActions.clearContentViewer())
    }
  }, [dispatch, enabled])
  const closeSource = () => {
    setSelectedReference(null)
    setHiddenSource(null)
    dispatch(ContentViewerActions.clearContentViewer())
  }
  const openReference = useCallback(
    (reference: TableReferenceTarget) => {
      const session = buildTableReferenceSession(reference, conversationId)
      if (!session) return false
      setSelectedReference({ id: reference.id ?? null, session })
      dispatch(ContentViewerActions.setContentViewerSession({ session }))
      setHiddenSource(null)
      setOpen(true)
      return true
    },
    [conversationId, dispatch]
  )
  return {
    open,
    width,
    setWidth,
    source: enabled ? source : null,
    sourceVisible: enabled && !!source && source !== hiddenSource,
    activeReferenceId: enabled && open && source !== hiddenSource && source === selectedReference?.session ? selectedReference?.id ?? null : null,
    selectSource: (selected: boolean) => setHiddenSource(selected ? null : source),
    closeSource,
    close: () => {
      setOpen(false)
      closeSource()
    },
    openAssistant: () => {
      setHiddenSource(source)
      setOpen(true)
    },
    openReference,
  }
}
